/**
 * Content block types for rendering
 */
export type ContentBlock = 
  | { type: 'paragraph'; content: string }
  | { type: 'ordered-list'; items: ListItem[] }
  | { type: 'unordered-list'; items: string[] }
  | { type: 'preformatted'; content: string };

export type NestedNumberedItem = {
  number: string; // e.g., "2.1."
  text: string;
};

export type ListItem = {
  text: string;
  itemNum?: string; // The number of this item (e.g., "2" from "(2)")
  additionalParagraphs?: string[]; // Paragraphs between main text and nested items
  nestedBullets?: string[];
  nestedNumbered?: NestedNumberedItem[];
};

/**
 * Patterns for detecting list items
 */
const LIST_PATTERNS = {
  // Matches: (1), (2), etc.
  numberedParens: /^\((\d+)\)\s+(.+)$/,
  // Matches: (1.), (2.), etc.
  numberedParensDot: /^\((\d+)\.\)\s+(.+)$/,
  // Matches: 1., 2., etc.
  numberedDot: /^(\d+)\.\s+(.+)$/,
  // Matches: 1), 2), etc.
  numberedParen: /^(\d+)\)\s+(.+)$/,
  // Matches: (1.1.), (1.2.), etc. - sub-numbered items
  subNumbered: /^\((\d+\.\d+\.)\)\s+(.+)$/,
  // Matches: •, -, * at start
  bullet: /^[•\-\*]\s+(.+)$/,
};

/**
 * Detects if a line is a sub-numbered list item and extracts content
 * Also returns the parent number (e.g., "2" from "(2.1.)") and the full number
 */
function detectSubNumberedItem(line: string): { isMatch: boolean; content: string; parentNum?: string; fullNum?: string } {
  const match = line.match(LIST_PATTERNS.subNumbered);
  if (match) {
    const fullNum = match[1]; // e.g., "2.1."
    const parentNum = fullNum.split('.')[0]; // e.g., "2"
    return { isMatch: true, content: match[2], parentNum, fullNum };
  }
  return { isMatch: false, content: line };
}

/**
 * Detects if a line is a numbered list item and extracts content
 * Also returns the item number (e.g., "2" from "(2)")
 */
function detectNumberedItem(line: string): { isMatch: boolean; content: string; itemNum?: string } {
  for (const pattern of [
    LIST_PATTERNS.numberedParens,
    LIST_PATTERNS.numberedParensDot,
    LIST_PATTERNS.numberedDot,
    LIST_PATTERNS.numberedParen,
  ]) {
    const match = line.match(pattern);
    if (match) {
      return { isMatch: true, content: match[2] || match[1], itemNum: match[1] };
    }
  }
  return { isMatch: false, content: line };
}

/**
 * Detects if a line is a bullet list item and extracts content
 */
function detectBulletItem(line: string): { isMatch: boolean; content: string } {
  const match = line.match(LIST_PATTERNS.bullet);
  if (match) {
    return { isMatch: true, content: match[1] };
  }
  return { isMatch: false, content: line };
}

/**
 * Cleans up text by fixing escaped quotes
 */
function cleanText(text: string): string {
  return text.replace(/\\"/g, '"').trim();
}

/**
 * Detects if a block of lines looks like an address or contact information block
 * These should preserve line breaks rather than joining with spaces
 */
function isAddressLikeBlock(lines: string[]): boolean {
  // Address blocks typically have multiple short lines
  if (lines.length < 3) return false;
  
  // Check if all lines are relatively short (address style)
  const allShort = lines.every(l => l.trim().length < 80);
  if (!allShort) return false;
  
  // Check for address/contact patterns (multilingual)
  const addressPatterns = [
    // German
    /Handelsregister|Registergericht|Geschäftsführer|Kontakt|Straße|Deutschland|Österreich|Schweiz/i,
    // English
    /contact|registered|managing director|address|street|germany|austria|switzerland/i,
    // French
    /Directeur|Responsable|Contact|Adresse|Allemagne|Autriche|Suisse/i,
    // Spanish
    /Director|Gerente|Contacto|Dirección|Alemania|Austria|Suiza/i,
    // Common patterns
    /\d{5}\s+\w+|c\/o|UG|GmbH|Ltd|Inc|@.*\.(io|com|de|org)/i,
  ];
  
  const hasAddressPattern = lines.some(l => 
    addressPatterns.some(pattern => pattern.test(l))
  );
  
  return hasAddressPattern;
}

/**
 * Parses text content into structured blocks for rendering
 * Handles mixed numbered lists, bullet lists, and paragraphs
 * 
 * Key features:
 * - Merges consecutive numbered items (even if separated by \n\n) into single list
 * - Handles multi-line list items
 * - Separates lists from paragraphs
 */
export function parseContentBlocks(text: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  
  // Split by double newlines to get raw blocks
  const rawBlocks = text.split('\n\n').filter(block => block.trim());
  
  let i = 0;
  while (i < rawBlocks.length) {
    const currentBlock = rawBlocks[i].trim();
    const lines = currentBlock.split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      i++;
      continue;
    }
    
    const firstLine = lines[0].trim();
    const numberedCheck = detectNumberedItem(firstLine);
    const bulletCheck = detectBulletItem(firstLine);
    
    if (numberedCheck.isMatch) {
      // Collect all consecutive numbered blocks into one list
      const items: ListItem[] = [];
      
      while (i < rawBlocks.length) {
        const block = rawBlocks[i].trim();
        const blockLines = block.split('\n').filter(line => line.trim());
        
        if (blockLines.length === 0) {
          i++;
          continue;
        }
        
        const blockFirstLine = blockLines[0].trim();
        const numberedItemCheck = detectNumberedItem(blockFirstLine);
        const subNumberedCheck = detectSubNumberedItem(blockFirstLine);
        const bulletCheck = detectBulletItem(blockFirstLine);
        
        // Check if this is a paragraph that might be part of the last item
        if (!numberedItemCheck.isMatch && !subNumberedCheck.isMatch && !bulletCheck.isMatch) {
          // This is a paragraph - check if it belongs to the last item
          if (items.length > 0) {
            const lastItem = items[items.length - 1];
            
            // Check if next block continues the numbered list
            if (i + 1 < rawBlocks.length) {
              const nextBlock = rawBlocks[i + 1].trim();
              const nextBlockLines = nextBlock.split('\n').filter(line => line.trim());
              
              if (nextBlockLines.length > 0) {
                const nextFirstLine = nextBlockLines[0].trim();
                const nextSubNumberedCheck = detectSubNumberedItem(nextFirstLine);
                const nextNumberedCheck = detectNumberedItem(nextFirstLine);
                
                // Attach to last item if:
                // 1. Next block is a sub-numbered item belonging to this item, OR
                // 2. Next block is a numbered item with a higher number (continuation of list)
                const isNextSubItem = nextSubNumberedCheck.isMatch && 
                                      nextSubNumberedCheck.parentNum === lastItem.itemNum;
                
                const isNextConsecutiveItem = nextNumberedCheck.isMatch && 
                                               lastItem.itemNum && 
                                               parseInt(nextNumberedCheck.itemNum || '0') > parseInt(lastItem.itemNum);
                
                if (isNextSubItem || isNextConsecutiveItem) {
                  if (!lastItem.additionalParagraphs) {
                    lastItem.additionalParagraphs = [];
                  }
                  lastItem.additionalParagraphs.push(cleanText(blockLines.join(' ')));
                  i++;
                  continue;
                }
              }
            }
          }
          
          // Not part of the list, stop collecting
          break;
        }
        
        // If this is a sub-numbered or bullet item, attach to last item
        if ((subNumberedCheck.isMatch || bulletCheck.isMatch) && !numberedItemCheck.isMatch && items.length > 0) {
          const lastItem = items[items.length - 1];
          
          for (const line of blockLines) {
            const trimmedLine = line.trim();
            const subNumCheck = detectSubNumberedItem(trimmedLine);
            const bulletItemCheck = detectBulletItem(trimmedLine);
            
            if (subNumCheck.isMatch) {
              if (!lastItem.nestedNumbered) lastItem.nestedNumbered = [];
              lastItem.nestedNumbered.push({
                number: subNumCheck.fullNum || '',
                text: cleanText(subNumCheck.content)
              });
            } else if (bulletItemCheck.isMatch) {
              if (!lastItem.nestedBullets) lastItem.nestedBullets = [];
              lastItem.nestedBullets.push(cleanText(bulletItemCheck.content));
            } else {
              // Continuation line
              if (lastItem.nestedNumbered && lastItem.nestedNumbered.length > 0) {
                lastItem.nestedNumbered[lastItem.nestedNumbered.length - 1].text += ' ' + trimmedLine;
              } else if (lastItem.nestedBullets && lastItem.nestedBullets.length > 0) {
                lastItem.nestedBullets[lastItem.nestedBullets.length - 1] += ' ' + trimmedLine;
              }
            }
          }
          
          i++;
          continue;
        }
        
        // Process this numbered block
        let currentItemText = '';
        let currentItemNum: string | undefined = undefined;
        let currentItemBullets: string[] = [];
        let currentItemNumbered: NestedNumberedItem[] = [];
        let inBulletSection = false;
        let inNumberedSection = false;
        
        for (const line of blockLines) {
          const trimmedLine = line.trim();
          const subNumberedItemCheck = detectSubNumberedItem(trimmedLine);
          const numberedCheck = !subNumberedItemCheck.isMatch ? detectNumberedItem(trimmedLine) : { isMatch: false, content: '' };
          const bulletItemCheck = detectBulletItem(trimmedLine);
          
          if (subNumberedItemCheck.isMatch) {
            inNumberedSection = true;
            inBulletSection = false;
            currentItemNumbered.push({
              number: subNumberedItemCheck.fullNum || '',
              text: cleanText(subNumberedItemCheck.content)
            });
          } else if (numberedCheck.isMatch) {
            // New numbered item - save previous
            if (currentItemText) {
              items.push({
                text: cleanText(currentItemText),
                itemNum: currentItemNum,
                nestedBullets: currentItemBullets.length > 0 ? currentItemBullets : undefined,
                nestedNumbered: currentItemNumbered.length > 0 ? currentItemNumbered : undefined
              });
            }
            currentItemText = numberedCheck.content;
            currentItemNum = numberedCheck.itemNum;
            currentItemBullets = [];
            currentItemNumbered = [];
            inBulletSection = false;
            inNumberedSection = false;
          } else if (bulletItemCheck.isMatch) {
            inBulletSection = true;
            inNumberedSection = false;
            currentItemBullets.push(cleanText(bulletItemCheck.content));
          } else {
            // Continuation line
            if (inNumberedSection && currentItemNumbered.length > 0) {
              currentItemNumbered[currentItemNumbered.length - 1].text += ' ' + trimmedLine;
            } else if (inBulletSection && currentItemBullets.length > 0) {
              currentItemBullets[currentItemBullets.length - 1] += ' ' + trimmedLine;
            } else if (currentItemText) {
              currentItemText += ' ' + trimmedLine;
            }
          }
        }
        
        // Save the last item
        if (currentItemText) {
          items.push({
            text: cleanText(currentItemText),
            itemNum: currentItemNum,
            nestedBullets: currentItemBullets.length > 0 ? currentItemBullets : undefined,
            nestedNumbered: currentItemNumbered.length > 0 ? currentItemNumbered : undefined
          });
        }
        
        i++;
      }
      
      if (items.length > 0) {
        blocks.push({ type: 'ordered-list', items });
      }
    } else if (bulletCheck.isMatch) {
      // Handle bullet list block (bullets stay within same block)
      const items: string[] = [];
      let currentItem = '';
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        const check = detectBulletItem(trimmedLine);
        
        if (check.isMatch) {
          if (currentItem) {
            items.push(cleanText(currentItem));
          }
          currentItem = check.content;
        } else {
          if (currentItem) {
            currentItem += ' ' + trimmedLine;
          }
        }
      }
      
      if (currentItem) {
        items.push(cleanText(currentItem));
      }
      
      if (items.length > 0) {
        blocks.push({ type: 'unordered-list', items });
      }
      
      i++;
    } else {
      // Check if this looks like an address/contact block that should preserve formatting
      if (isAddressLikeBlock(lines)) {
        const content = cleanText(lines.join('\n'));
        if (content) {
          blocks.push({ type: 'preformatted', content });
        }
      } else {
        // Regular paragraph
        const content = cleanText(lines.join(' '));
        if (content) {
          blocks.push({ type: 'paragraph', content });
        }
      }
      i++;
    }
  }
  
  return blocks;
}
